import { getApp, getApps, initializeApp } from 'firebase/app';
import { collection, connectFirestoreEmulator, getDocs, getFirestore, initializeFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { connectStorageEmulator, getDownloadURL, getStorage, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { connectAuthEmulator, signInWithEmailAndPassword } from 'firebase/auth';
import type { OrganizerEventTemplate } from './types';
import { processImageForUpload, type ImageUploadPurpose } from './utils/imagePipeline';
import { createFirebaseAuth } from './utils/authPersistence';
import { firebaseConfig } from './firebaseConfig';

export const firebase = getApps().length ? getApp() : initializeApp(firebaseConfig);

// createFirebaseAuth has a native (AsyncStorage-backed) implementation and a
// .web.ts sibling (browser-persistence-backed) — see src/utils/authPersistence.ts.
export const auth = createFirebaseAuth(firebase);

// Android emulators (and some real devices/networks) frequently fail to
// sustain Firestore's default gRPC-over-WebChannel streaming transport,
// logging noisy "WebChannelConnection RPC ... transport errored" warnings
// on every reconnect even though the SDK's auto-retry ultimately succeeds.
// Forcing long-polling avoids the flaky streaming path entirely. initializeFirestore
// throws if a Firestore instance already exists for this app (e.g. Fast
// Refresh), so fall back to getFirestore() in that case — same pattern as
// createAuth() above.
function createFirestore() {
  try {
    return initializeFirestore(firebase, {
      experimentalAutoDetectLongPolling: true,
    });
  } catch {
    return getFirestore(firebase);
  }
}
export const firestore = createFirestore();
export const storage = getStorage(firebase);

// Local Firebase emulators — development of the web bundle only. The flag is
// defined solely by webpack.config.web.js for `TONYTE_USE_EMULATORS=1 npm run
// web` (never in production builds); Metro/native builds don't define it at
// all, so the typeof guard keeps every shipped app on the real backend.
declare const __TONYTE_USE_EMULATORS__: boolean | undefined;
if (typeof __TONYTE_USE_EMULATORS__ !== 'undefined' && __TONYTE_USE_EMULATORS__) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  // Emulator accounts only — lets local tooling sign in without the UI.
  (globalThis as any).__tonyteEmulatorSignIn = (email: string, password: string) => signInWithEmailAndPassword(auth, email, password);
  console.warn('[Tonyte] Using local Firebase emulators (TONYTE_USE_EMULATORS=1)');
}
// @firebase/functions's FunctionsService constructor does
// `new URL(regionOrCustomDomain)` and relies on it THROWING for a bare
// region string like 'us-central1' (falling into a catch that sets
// `this.region` correctly). React Native's built-in URL polyfill
// (react-native/Libraries/Blob/URL.js) does not throw for non-absolute
// strings — it silently accepts them with an empty `origin` and `/`
// `pathname` — so the SDK instead takes the "custom domain" branch with an
// empty domain, and every httpsCallable() request gets built as a bare
// `/functionName` path with no scheme or host. fetch() can't resolve that
// (no page origin to resolve a relative URL against, unlike a browser), so
// it throws `TypeError: Network request failed`, which
// @firebase/functions's postJSON() swallows into a generic
// FirebaseError('internal') with no useful message — this was breaking
// EVERY authenticated callable-function call in the app silently. Passing
// the full origin instead of a bare region routes around the broken
// `new URL()` region-detection path entirely: a genuinely absolute URL
// string parses the same (correctly) under both RN's polyfill and a real
// URL implementation.
export const functions = getFunctions(firebase, 'https://us-central1-triptribe-8babd.cloudfunctions.net');
// Phase 3C (region migration, planning doc: FUNCTIONS_REGION_MIGRATION_PLAN.md
// §6): a second Functions instance pointed at europe-west3, used only by the
// 11 Tier 3 call sites that have migrated to a europe-west3 (*Eu) function.
// `functions` above keeps pointing at us-central1 indefinitely — old
// installed app builds never get a europe-west3 instance and keep calling
// the untouched us-central1 originals until they update.
export const functionsEu = getFunctions(firebase, 'https://europe-west3-triptribe-8babd.cloudfunctions.net');
export { httpsCallable };

export const FIRESTORE_COLLECTIONS = {
  users: 'users',
  activities: 'activities',
  plans: 'plans',
  activityInterests: 'activityInterests',
  matches: 'matches',
  eventChats: 'eventChats',
  planChats: 'planChats',
  deletedActivities: 'deletedActivities',
  blocks: 'blocks',
  notifications: 'notifications',
  vibeRequests: 'vibeRequests',
  privateChats: 'privateChats',
  vibeRelationships: 'vibeRelationships',
  dayVibeQuestions: 'dayVibeQuestions',
  dayVibeResponses: 'dayVibeResponses',
  dayVibeDrafts: 'dayVibeDrafts',
  dayVibeMatches: 'dayVibeMatches',
  vibeIcebreakers: 'vibeIcebreakers',
  planJoinRequests: 'planJoinRequests',
  partnerCommunityChats: 'partnerCommunityChats',
} as const;

export const PUBLIC_ACTIVITY_COLLECTIONS = [
  'activities',
  'officialEvents',
  'events',
] as const;

export const DEFAULT_AVATAR =
  'https://randomuser.me/api/portraits/men/32.jpg';
export const DEFAULT_COVER =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&q=80&w=1080';

// Single shared image-upload path for this app. Every caller below routes
// through `processImageForUpload` (../utils/imagePipeline.ts) FIRST — that
// re-encodes the photo (stripping all EXIF/IPTC/XMP metadata) and applies
// the size/quality preset for `purpose` — and only the processed copy is
// ever fetched into a blob and uploaded. There is no direct-to-`uploadBytes`
// path left for images in this app; the storage path is always built from
// the ORIGINAL uri's extension (before processing), since the processed
// file is always a re-encoded JPEG and we still want the Storage object
// name to line up with the app's existing `.jpg`-by-default convention.
async function uploadImageToStorage(
  uri: string,
  storagePath: string,
  purpose: ImageUploadPurpose,
): Promise<{ downloadUrl: string; storagePath: string }> {
  const processedUri = await processImageForUpload(uri, purpose);
  const response = await fetch(processedUri);
  const blob = await response.blob();
  const imageRef = ref(storage, storagePath);
  // Processed images are always JPEG; RN blobs can arrive with an empty type,
  // and storage.rules (venues/) require an image content type.
  await uploadBytes(imageRef, blob, { contentType: blob.type || 'image/jpeg' });
  const downloadUrl = await getDownloadURL(imageRef);
  return { downloadUrl, storagePath };
}

export async function uploadUserImage(
  userId: string,
  uri: string,
  kind: 'profile' | 'cover',
): Promise<{ downloadUrl: string; storagePath: string }> {
  const extension = uri.split('?')[0]?.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExtension = ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(extension) ? extension : 'jpg';
  const storagePath = `users/${userId}/${kind}/${Date.now()}.${safeExtension}`;
  return uploadImageToStorage(uri, storagePath, kind);
}

// Partner venue cover / profile picture. Stored under venues/{venueId}/, which
// storage.rules restricts to that venue's owner and admins.
export async function uploadVenueImage(
  venueId: string,
  uri: string,
  kind: 'logo' | 'cover',
): Promise<{ downloadUrl: string; storagePath: string }> {
  const extension = uri.split('?')[0]?.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExtension = ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(extension) ? extension : 'jpg';
  const storagePath = `venues/${venueId}/${kind}-${Date.now()}.${safeExtension}`;
  return uploadImageToStorage(uri, storagePath, kind === 'logo' ? 'profile' : 'cover');
}

export async function uploadChatImage(
  chatId: string,
  senderId: string,
  uri: string,
): Promise<{ downloadUrl: string; storagePath: string }> {
  const extension = uri.split('?')[0]?.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExtension = ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(extension) ? extension : 'jpg';
  const storagePath = `chatImages/${chatId}/${senderId}-${Date.now()}.${safeExtension}`;
  return uploadImageToStorage(uri, storagePath, 'chat');
}

export async function uploadChatAudio(
  chatId: string,
  senderId: string,
  uri: string,
  onProgress?: (progress: number) => void,
): Promise<{ downloadUrl: string; storagePath: string }> {
  const extension = uri.split('?')[0]?.split('.').pop()?.toLowerCase() || 'm4a';
  const safeExtension = ['m4a', 'aac', 'mp4', 'caf', 'wav', 'webm', 'opus'].includes(extension) ? extension : 'm4a';
  const storagePath = `chatAudio/${chatId}/${senderId}-${Date.now()}.${safeExtension}`;
  const response = await fetch(uri);
  const blob = await response.blob();
  const audioRef = ref(storage, storagePath);

  await new Promise<void>((resolve, reject) => {
    const task = uploadBytesResumable(audioRef, blob, {
      contentType: safeExtension === 'wav' ? 'audio/wav' : safeExtension === 'webm' ? 'audio/webm' : 'audio/mp4',
    });
    task.on(
      'state_changed',
      snapshot => {
        const progress = snapshot.totalBytes > 0 ? snapshot.bytesTransferred / snapshot.totalBytes : 0;
        onProgress?.(Math.max(0, Math.min(1, progress)));
      },
      reject,
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(audioRef);
  return { downloadUrl, storagePath };
}

// Promotion cover (admin only — storage.rules `promotions/` rejects anyone
// else). Same metadata-stripping pipeline as every other upload.
export async function uploadPromotionCover(
  promotionId: string,
  uri: string,
): Promise<{ downloadUrl: string; storagePath: string }> {
  return uploadImageToStorage(uri, `promotions/${promotionId}/cover-${Date.now()}.jpg`, 'cover');
}

// Event cover from the app's event editor — same Storage folder the partner
// portal uses (storage.rules: any signed-in user may write there).
export async function uploadEventCover(
  ownerUid: string,
  uri: string,
): Promise<{ downloadUrl: string; storagePath: string }> {
  return uploadImageToStorage(uri, `activities/official_event/${ownerUid}/cover-${Date.now()}.jpg`, 'cover');
}

export async function uploadFaceVerificationImage(
  userId: string,
  uri: string,
  pose: 'selfie' | 'front' | 'left' | 'right',
): Promise<{ downloadUrl: string; storagePath: string }> {
  const extension = uri.split('?')[0]?.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExtension = ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(extension) ? extension : 'jpg';
  const storagePath = `faceVerificationRequests/${userId}/${Date.now()}-${pose}.${safeExtension}`;
  return uploadImageToStorage(uri, storagePath, 'verification');
}

export async function uploadActivityCoverImage(
  _userId: string,
  _uri: string,
  _entryType: string,
): Promise<string> {
  return DEFAULT_COVER;
}

function normalizeTemplate(id: string, data: Record<string, any>): OrganizerEventTemplate {
  const toStr = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    id,
    templateName: toStr(data.templateName),
    clubName: toStr(data.clubName),
    clubId: toStr(data.clubId),
    address: toStr(data.address),
    city: toStr(data.city),
    cityKey: toStr(data.cityKey),
    mapsUrl: toStr(data.mapsUrl),
    eventTitle: toStr(data.eventTitle),
    description: toStr(data.description),
    category: (['social', 'adventure', 'culture', 'food', 'nightlife'].includes(data.category)
      ? data.category
      : 'social') as OrganizerEventTemplate['category'],
    tags: Array.isArray(data.tags) ? data.tags.filter((t: unknown): t is string => typeof t === 'string') : [],
    coverImageUrl: toStr(data.coverImageUrl),
    defaultStartTime: toStr(data.defaultStartTime),
    defaultEndTime: toStr(data.defaultEndTime),
    defaultPrice: toStr(data.defaultPrice),
    defaultAgeGroup: toStr(data.defaultAgeGroup),
    defaultPrivacy: toStr(data.defaultPrivacy),
    sourceEventId: toStr(data.sourceEventId),
    createdAt: toStr(data.createdAt),
    updatedAt: toStr(data.updatedAt),
  };
}

export async function readOrganizerEventTemplates(organizerId: string): Promise<OrganizerEventTemplate[]> {
  const snap = await getDocs(collection(firestore, 'organizerLibraries', organizerId, 'eventTemplates'));
  return snap.docs.map(d => normalizeTemplate(d.id, d.data()));
}
